import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject, of } from 'rxjs'
import { AudioQueueItem, QueueState } from './audio-queue.types'
import {
  AudioQueueStateMachineService,
  AudioQueueState,
} from './audio-queue-state-machine.service'

/**
 * 音声再生キュー管理サービス
 *
 * 責務:
 * - 音声アイテムのFIFOキュー管理（エンキュー、デキュー、クリア）
 * - 音声アイテムメタデータ管理（type、interruptible、source）
 * - 音声再生オーケストレーション（FIFO再生、割り込み処理）
 * - キュー状態のObservable公開（キュー長、現在再生中アイテム、キュー状態）
 * - キュー完了イベントの発行
 * - Cloud STTモード連携用のaiTurnCompleteReceivedフラグ管理
 *
 * 主要機能:
 * - タスク1.1: 音声キュー管理機能（エンキュー、デキュー、クリア、Observables公開）
 * - タスク1.2: 音声再生オーケストレーション（FIFO再生、フィラー割り込み、キュー完了イベント）
 * - タスク1.3: Cloud STTモード連携（キュー完了 + aiTurnComplete受信での次ターン開始準備完了イベント発行）
 *
 * 依存関係:
 * - Inbound: RoleplayWebSocketFacadeService, CloudSttTurnCoordinatorService
 * - Outbound: AudioQueueStateMachineService（フェーズ1後半で統合完了）
 *
 * 使用例（基本的な音声キュー操作）:
 * ```typescript
 * constructor(private audioQueue: AudioQueueService) {
 *   // キュー長の監視
 *   this.audioQueue.getQueueLength().subscribe(length => {
 *     console.log('Queue length:', length);
 *   });
 *
 *   // キュー完了イベントの購読
 *   this.audioQueue.getQueueCompleted().subscribe(() => {
 *     console.log('Queue playback completed');
 *   });
 * }
 *
 * enqueueAndPlay() {
 *   const item: AudioQueueItem = {
 *     type: 'response',
 *     audioData: 'base64-or-url',
 *     interruptible: false,
 *     source: 'ai-response'
 *   };
 *   this.audioQueue.enqueue(item);
 *   this.audioQueue.playNext().subscribe(() => {
 *     console.log('Playback completed');
 *   });
 * }
 * ```
 *
 * 使用例（Cloud STTモード連携）:
 * ```typescript
 * constructor(private audioQueue: AudioQueueService) {
 *   // 次のターン開始準備完了イベントの購読
 *   this.audioQueue.getReadyForNextTurn().subscribe(() => {
 *     console.log('Ready for next turn - starting new user turn');
 *     this.startNextUserTurn();
 *   });
 * }
 *
 * // aiTurnCompleteイベント受信時
 * onAiTurnComplete() {
 *   this.audioQueue.setAiTurnCompleteReceived(true);
 *   // キューが空なら即座にreadyForNextTurnイベントが発火
 *   // キューにアイテムがあれば、全て再生完了後に発火
 * }
 * ```
 *
 * @see design.md AudioQueueService section
 * @see requirements.md 要件5: 音声キューオーケストレーション
 */
@Injectable({
  providedIn: 'root',
})
export class AudioQueueService {
  // 音声キューデータ構造（FIFO）
  private queue: AudioQueueItem[] = []

  constructor(private stateMachine: AudioQueueStateMachineService) {}

  // 現在再生中のアイテム
  private currentItemSubject = new BehaviorSubject<AudioQueueItem | null>(null)

  // キュー長
  private queueLengthSubject = new BehaviorSubject<number>(0)

  // キュー状態
  private queueStateSubject = new BehaviorSubject<QueueState>('IDLE')

  // キュー完了イベント
  private queueCompletedSubject = new Subject<void>()

  // Cloud STT専用: 次のターン開始準備完了イベント
  private readyForNextTurnSubject = new Subject<void>()

  // Cloud STT専用: aiTurnComplete受信フラグ
  private aiTurnCompleteReceived = false

  // Cloud STT専用: キュー完了済みフラグ（aiTurnComplete待ち状態を追跡）
  private queueCompletedAndWaiting = false

  // 音声再生制御用のSubject
  private stopPlaybackSubject = new Subject<void>()

  /**
   * 音声アイテムをキューに追加
   * @param item 音声アイテム（メタデータ含む）
   */
  enqueue(item: AudioQueueItem): void {
    const wasEmpty = this.queue.length === 0
    const isNotCurrentlyPlaying = this.currentItemSubject.value == null
    const isIdleState = this.queueStateSubject.value === 'IDLE'

    this.queue.push(item)
    this.queueLengthSubject.next(this.queue.length)

    // フィラー音声再生中にAI応答が到着した場合、割り込み処理を実行
    const currentItem = this.currentItemSubject.value
    if (
      currentItem != null &&
      currentItem.type === 'filler' &&
      currentItem.interruptible &&
      item.type === 'response'
    ) {
      this.handleInterruption()
      return // handleInterruption calls playNext internally
    }

    // Bug fix: キューが空で何も再生中でない場合、自動的に再生を開始
    // これにより、コンポーネントがplayNext()を手動で呼び出す必要がなくなる
    // 条件: キューが空だった AND 何も再生中でない AND IDLE状態
    if (wasEmpty && isNotCurrentlyPlaying && isIdleState) {
      this.playNext().subscribe()
    }
  }

  /**
   * キューをクリア（全アイテム削除）
   *
   * キュー内の全てのアイテムを削除し、状態を初期化します。
   * セッション間で状態をリセットするために使用されます。
   */
  clear(): void {
    this.queue = []
    this.queueLengthSubject.next(0)
    this.currentItemSubject.next(null)

    // Cloud STTモード用のフラグもリセット（セッション間のクリーンアップ）
    this.aiTurnCompleteReceived = false
    this.queueCompletedAndWaiting = false

    // 状態をIDLEに戻す（reset()を使用して無条件にIDLEに設定）
    this.queueStateSubject.next('IDLE')
    this.stateMachine.reset()
  }

  /**
   * 次の音声を再生（FIFO順）
   *
   * キューの先頭アイテムを取得し、再生を開始します。
   * 実際の音声再生は外部（コンポーネント）が行い、完了したら
   * completeCurrentPlayback() を呼び出す必要があります。
   *
   * @returns 再生完了のObservable（キューが空の場合は即座に完了）
   */
  playNext(): Observable<void> {
    // キューが空の場合は何もしない
    if (this.queue.length === 0) {
      return of(void 0)
    }

    // FIFO: キューの先頭アイテムを取得（デキューはせず、再生完了後に削除）
    const item = this.queue[0]

    // 現在再生中のアイテムを設定
    this.currentItemSubject.next(item)

    // 状態を更新（ステートマシンと同期）
    const newState: QueueState =
      item.type === 'filler' ? 'PLAYING_FILLER' : 'PLAYING_RESPONSE'
    this.queueStateSubject.next(newState)

    // ステートマシンの状態も更新
    const stateMachineState: AudioQueueState =
      item.type === 'filler'
        ? AudioQueueState.PLAYING_FILLER
        : AudioQueueState.PLAYING_RESPONSE
    this.stateMachine.transition(stateMachineState)

    // 実際の再生は外部で行われるため、即座に完了を返す
    // 外部はcompleteCurrentPlayback()を呼び出して再生完了を通知する必要がある
    return of(void 0)
  }

  /**
   * 現在再生中のアイテムの再生完了を通知
   *
   * 外部（コンポーネント）から実際の音声再生が完了したときに呼び出されます。
   * キューから完了したアイテムを削除し、次のアイテムがあれば自動的に再生を開始します。
   */
  completeCurrentPlayback(): void {
    // 再生完了後の処理を実行
    this.completePlayback()

    // 次のアイテムがあれば自動的に再生開始
    if (this.queue.length > 0) {
      this.playNext().subscribe()
    }
  }

  /**
   * 再生完了時の処理（内部メソッド）
   *
   * 再生が完了したアイテムをキューから削除し、状態をIDLEに戻します。
   * キューが空になった場合は、キュー完了イベントを発行します。
   *
   * Cloud STTモード連携:
   * キュー完了時にaiTurnCompleteReceivedフラグをチェックし、
   * フラグがtrueの場合は次のターン開始準備完了イベントを発行します。
   */
  private completePlayback(): void {
    // キューから完了したアイテムを削除
    this.queue.shift()
    this.queueLengthSubject.next(this.queue.length)

    // 現在のアイテムをクリア
    this.currentItemSubject.next(null)

    // 状態をIDLEに戻す（reset()を使用して無条件にIDLEに設定）
    this.queueStateSubject.next('IDLE')
    this.stateMachine.reset()

    // キューが空になったら完了イベントを発行
    if (this.queue.length === 0) {
      this.queueCompletedSubject.next()

      // Cloud STTモード連携: キュー完了 + aiTurnComplete受信済みの場合
      this.checkAndEmitReadyForNextTurn()
    }
  }

  /**
   * フィラー音声の割り込み処理（内部メソッド）
   *
   * フィラー音声再生中に AI応答が到着した場合、フィラー音声を即座に停止し、
   * AI応答を優先的に再生します。これにより、ユーザーは最新のAI応答を
   * 待たされることなく聞くことができます。
   *
   * 処理フロー:
   * 1. 状態をINTERRUPTEDに変更
   * 2. 現在のフィラー音声を停止
   * 3. フィラーアイテムをキューから削除
   * 4. 次のアイテム（AI応答）を即座に再生
   */
  private handleInterruption(): void {
    // 状態をINTERRUPTEDに変更（ステートマシンと同期）
    this.queueStateSubject.next('INTERRUPTED')
    this.stateMachine.transition(AudioQueueState.INTERRUPTED)

    // 現在のフィラー音声を停止
    this.stopPlaybackSubject.next()

    // フィラーアイテムをキューから削除
    this.queue.shift()
    this.queueLengthSubject.next(this.queue.length)

    // 次のアイテム（AI応答）を即座に再生
    if (this.queue.length > 0) {
      this.playNext().subscribe()
    }
  }

  /**
   * 現在再生中のアイテムを取得
   * @returns 再生中アイテムのObservable（再生なしならnull）
   */
  getCurrentItem(): Observable<AudioQueueItem | null> {
    return this.currentItemSubject.asObservable()
  }

  /**
   * キューの長さを取得
   * @returns キュー長のObservable
   */
  getQueueLength(): Observable<number> {
    return this.queueLengthSubject.asObservable()
  }

  /**
   * キュー完了イベントを取得
   * @returns キュー完了時に発火するObservable
   */
  getQueueCompleted(): Observable<void> {
    return this.queueCompletedSubject.asObservable()
  }

  /**
   * Cloud STT専用: aiTurnComplete受信フラグを設定
   *
   * キュー完了後にこのフラグがtrueに設定された場合、即座に次のターン開始準備完了イベントを発行します。
   * これにより「aiTurnComplete先行 → キュー完了」「キュー完了先行 → aiTurnComplete」の両方のシーケンスに対応します。
   *
   * @param received 受信済みならtrue
   */
  setAiTurnCompleteReceived(received: boolean): void {
    this.aiTurnCompleteReceived = received

    // フラグが設定されたら即座にチェック
    // AI応答がスキップされた場合（empty_transcript等）、キューにアイテムがエンキューされないため
    // queueCompletedAndWaiting が true にならない。無条件にチェックすることで
    // 「キュー空 + aiTurnComplete受信済み」の条件を即座に評価し、次のターンを開始できる。
    if (received) {
      this.checkAndEmitReadyForNextTurn()
    }
  }

  /**
   * Cloud STT専用: 次のターン開始準備完了イベントを取得
   *
   * このイベントは、以下の2つの条件が両方満たされた時に発火します:
   * 1. 音声キューが空になった（全ての音声再生が完了）
   * 2. aiTurnCompleteイベントをサーバーから受信した
   *
   * イベント発火後、aiTurnCompleteReceivedフラグは自動的にリセットされます。
   *
   * @returns 次のターン開始準備完了イベントのObservable
   */
  getReadyForNextTurn(): Observable<void> {
    return this.readyForNextTurnSubject.asObservable()
  }

  /**
   * Cloud STTモード連携: キュー完了とaiTurnComplete受信をチェックし、
   * 両方の条件が満たされていれば次のターン開始準備完了イベントを発行
   *
   * 処理フロー:
   * 1. キューが空かチェック
   * 2. aiTurnCompleteReceivedフラグをチェック
   * 3. 両方trueなら readyForNextTurn イベントを発行
   * 4. フラグをリセットして次のターンサイクルに備える
   *
   * このメソッドは以下のタイミングで呼ばれます:
   * - completePlayback(): キューが空になった時
   * - setAiTurnCompleteReceived(): aiTurnCompleteフラグがtrueに設定された時
   */
  private checkAndEmitReadyForNextTurn(): void {
    const isQueueEmpty = this.queue.length === 0

    if (isQueueEmpty && this.aiTurnCompleteReceived) {
      // フラグをリセット（次のターンサイクルに備える）
      // 注意: イベント発行前にリセットする必要がある（Subjectは同期的に発火するため）
      this.aiTurnCompleteReceived = false
      this.queueCompletedAndWaiting = false

      // 両方の条件が満たされたので次のターン開始準備完了イベントを発行
      this.readyForNextTurnSubject.next()
    } else if (isQueueEmpty && !this.aiTurnCompleteReceived) {
      // キューは完了したがaiTurnCompleteはまだ受信していない
      // 待機フラグを立てる
      this.queueCompletedAndWaiting = true
    }
  }
}
